import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
  Video,
  XCircle,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface CampaignAiGuidelinesModalProps {
  open: boolean;
  joined: boolean;
  onDismiss: (dismissAll: boolean) => void;
  onAcknowledge: (dismissAll: boolean) => void;
}

const PERMITTED_ITEM_KEYS = [
  'campaign.aiGuidelines.permittedItem1',
  'campaign.aiGuidelines.permittedItem2',
  'campaign.aiGuidelines.permittedItem3',
] as const;

const FORBIDDEN_ITEM_KEYS = [
  'campaign.aiGuidelines.forbiddenItem1',
  'campaign.aiGuidelines.forbiddenItem2',
  'campaign.aiGuidelines.forbiddenItem3',
] as const;

export function CampaignAiGuidelinesModal({
  open,
  joined,
  onDismiss,
  onAcknowledge,
}: CampaignAiGuidelinesModalProps) {
  const { t } = useTranslation();
  const [dismissAll, setDismissAll] = useState(false);

  useEffect(() => {
    if (open) setDismissAll(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onDismiss(dismissAll);
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-1.5rem)] max-w-xl flex-col gap-0 overflow-hidden border-primary/35 bg-card p-0 shadow-glow sm:rounded-2xl"
      >
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-5 sm:p-6">
          <DialogHeader className="pr-8 text-left">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 shadow-primary">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 pt-0.5">
                <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[10px] font-bold tracking-wide text-primary">
                  <ShieldCheck className="h-3 w-3" />
                  {t('campaign.aiGuidelines.eyebrow')}
                </div>
                <DialogTitle className="text-xl font-bold leading-tight text-zinc-50 sm:text-2xl">
                  {t('campaign.aiGuidelines.title')}
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-5 flex items-start gap-3 rounded-xl border border-primary/30 bg-gradient-to-r from-primary/[0.08] to-[hsl(var(--brand-secondary)/0.06)] p-3.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15">
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-primary">
                  {t('campaign.aiGuidelines.accessTitle')}
                </p>
                <span className="rounded border border-cyan-400/30 bg-cyan-400/10 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-cyan-300">
                  {t('campaign.aiGuidelines.accessBadge')}
                </span>
              </div>
              <p className="mt-0.5 text-xs leading-relaxed text-zinc-300">
                {t('campaign.aiGuidelines.accessDescription')}
              </p>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {[
              { icon: Sparkles, key: 'allPlans' },
              { icon: LockKeyhole, key: 'openCampaigns' },
              { icon: Video, key: 'eligibleVideos' },
            ].map(({ icon: Icon, key }) => (
              <div
                key={key}
                className="flex min-h-14 items-center justify-center gap-2 rounded-lg border border-border bg-secondary/60 px-2.5 py-2 text-center"
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="text-[10px] font-semibold leading-tight text-primary">
                  {t(`campaign.aiGuidelines.${key}`)}
                </span>
              </div>
            ))}
          </div>

          <p className="mt-3 rounded-xl border border-border bg-secondary/55 p-3.5 text-sm leading-relaxed text-muted-foreground">
            {t('campaign.aiGuidelines.explanation')}
          </p>

          <div className="mt-3 space-y-2.5">
            <section className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-3.5">
              <h3 className="flex items-center gap-1.5 text-xs font-bold text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t('campaign.aiGuidelines.permittedTitle')}
              </h3>
              <ul className="mt-2 space-y-1.5">
                {PERMITTED_ITEM_KEYS.map((key) => (
                  <li key={key} className="flex items-start gap-2 text-xs leading-relaxed text-zinc-300">
                    <span className="mt-0.5 font-bold text-emerald-400">•</span>
                    {t(key)}
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-xl border border-red-500/35 bg-red-950/25 p-3.5">
              <h3 className="flex items-center gap-1.5 text-xs font-bold text-red-400">
                <XCircle className="h-3.5 w-3.5" />
                {t('campaign.aiGuidelines.forbiddenTitle')}
              </h3>
              <ul className="mt-2 space-y-1.5">
                {FORBIDDEN_ITEM_KEYS.map((key) => (
                  <li key={key} className="flex items-start gap-2 text-xs leading-relaxed text-zinc-300">
                    <span className="mt-0.5 font-bold text-red-400">•</span>
                    {t(key)}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-primary/30 bg-gradient-to-r from-primary/[0.07] to-[hsl(var(--brand-secondary)/0.05)] p-3">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <p className="text-xs font-medium leading-relaxed text-zinc-200">
              <strong className="text-zinc-100">{t('campaign.aiGuidelines.importantPrefix')}</strong>{' '}
              <span className="text-primary">{t('campaign.aiGuidelines.importantMessage')}</span>
            </p>
          </div>

          <div className="mt-3.5 flex items-center gap-2.5">
            <Checkbox
              id="campaign-ai-guidelines-dismiss-all"
              checked={dismissAll}
              onCheckedChange={(checked) => setDismissAll(checked === true)}
              className="border-muted-foreground data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
            />
            <Label
              htmlFor="campaign-ai-guidelines-dismiss-all"
              className="cursor-pointer text-xs font-normal text-zinc-400"
            >
              {t('campaign.aiGuidelines.dismissAll')}
            </Label>
          </div>

          <Button
            type="button"
            variant="gradient"
            className="mt-4 h-11 w-full font-bold active:scale-[0.98]"
            onClick={() => onAcknowledge(dismissAll)}
          >
            <Sparkles className="h-4 w-4" />
            {t(joined
              ? 'campaign.aiGuidelines.acknowledgeCta'
              : 'campaign.aiGuidelines.joinCta')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
